import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { httpGetPool } from 'services/pools';
import { httpGetPumps } from 'services/pumps';
import { useAuthContext } from 'contexts/AuthContext';
import { useStoreState } from 'easy-peasy';
import EditPoolModal from './EditPoolModal';
import './PoolDetailPanel.scss';

export default function PoolDetailPanel({ poolId, onBack }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuthContext();
  const [pool, setPool] = useState(null);
  const [pumps, setPumps] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastFetchedPoolIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  const poolsFromStore = useStoreState((state) => state.pools.pools);

  useEffect(() => {
    if (!poolId || !poolsFromStore) return;

    const poolFromStore = poolsFromStore.find((p) => p.id === parseInt(poolId, 10));
    if (poolFromStore && pool && poolFromStore.id === pool.id) {
      setPool(poolFromStore);
    }
  }, [poolsFromStore, poolId, pool]);

  useEffect(() => {
    if (!poolId) {
      setLoading(false);
      lastFetchedPoolIdRef.current = null;
      setPool(null);
      setPumps([]);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    if (lastFetchedPoolIdRef.current === parseInt(poolId, 10)) {
      return;
    }

    const fetchPoolData = async () => {
      try {
        isFetchingRef.current = true;
        lastFetchedPoolIdRef.current = parseInt(poolId, 10);
        setLoading(true);

        const poolResponse = await httpGetPool(poolId);
        setPool(poolResponse.data);

        const pumpsResponse = await httpGetPumps({ current_pool: poolId });
        const pumpsData = Array.isArray(pumpsResponse.data)
          ? pumpsResponse.data
          : pumpsResponse.data.results || [];
        const activePumps = pumpsData.filter((pump) => pump.status !== 7);
        setPumps(activePumps);

        setError(null);
      } catch (err) {
        console.error('Error fetching pool data:', err);
        setError(t('pool_detail_error_load'));
        lastFetchedPoolIdRef.current = null;
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchPoolData();
  }, [poolId, t]);

  const handlePumpClick = (pId) => {
    navigate(`/dashboard/pumps/${pId}`);
  };

  const handleVaihdaClick = (e, pId) => {
    e.stopPropagation();
    navigate(`/dashboard/pools/${poolId}/replace-pump/${pId}`);
  };

  useEffect(() => {
    const replaceResult = location.state?.replaceResult;
    if (!replaceResult || !poolId) return;
    const { replacedPumpId, newPump } = replaceResult;
    setPumps((prev) => prev.map((p) => (p.id === replacedPumpId ? newPump : p)));
    const timeoutId = setTimeout(() => {
      navigate(location.pathname, { replace: true, state: {} });
    }, 280);
    return () => clearTimeout(timeoutId);
  }, [location.state?.replaceResult, poolId, location.pathname, navigate]);

  // Repairer (3) may not edit pool metadata.
  const canEditPoolMetadata = userRole !== 3;
  const canReplacePumpInPool = [1, 2, 4, 5].includes(Number(userRole));

  if (!poolId) {
    return (
      <div className="pool-detail-panel">
        <div className="pool-detail-placeholder">
          <i className="fas fa-hand-pointer" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }} />
          <h3>{t('pool_detail_placeholder_title')}</h3>
          <p>{t('pool_detail_placeholder_hint')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pool-detail-panel">
        <div className="details-header">
          {onBack && (
            <button type="button" className="back-btn" onClick={onBack}>
              ←
            </button>
          )}
          <h2 className="details-title">{t('pool_detail_title')}</h2>
          {canEditPoolMetadata ? (
            <button type="button" className="edit-btn" disabled title={t('ui_loading')}>
              <i className="fas fa-pen" />
            </button>
          ) : (
            <div style={{ width: '48px' }} />
          )}
        </div>
        <div className="pool-details-content">
          <div className="text-center py-5">{t('ui_loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="pool-detail-panel">
        <div className="details-header">
          {onBack && (
            <button type="button" className="back-btn" onClick={onBack}>
              ←
            </button>
          )}
          <h2 className="details-title">{t('pool_detail_title')}</h2>
          {canEditPoolMetadata ? (
            <button type="button" className="edit-btn" disabled title={t('pool_detail_error_title')}>
              <i className="fas fa-pen" />
            </button>
          ) : (
            <div style={{ width: '48px' }} />
          )}
        </div>
        <div className="pool-details-content">
          <div
            style={{
              color: '#b71c1c',
              backgroundColor: '#fde0e0',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '0.9rem',
            }}
          >
            {error || t('pool_detail_not_found')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pool-detail-panel">
      <div className="details-header">
        {onBack && (
          <button type="button" className="back-btn" onClick={onBack}>
            ←
          </button>
        )}
        <h2 className="details-title">{t('pool_detail_title')}</h2>
        {canEditPoolMetadata ? (
          <button
            type="button"
            className="edit-btn"
            onClick={() => setShowEditModal(true)}
            disabled={loading || !pool}
            title={loading || !pool ? t('ui_loading') : t('pool_modal_edit_title')}
          >
            <i className="fas fa-pen" />
          </button>
        ) : (
          <div style={{ width: '48px' }} />
        )}
      </div>
      <div className="pool-details-content">
        <div className="details-table">
          <div className="details-row">
            <span className="details-label">{t('pump_detail_label_name')}</span>
            <span>{pool.name}</span>
          </div>
          <div className="details-row">
            <span className="details-label">{t('pool_detail_label_mine')}</span>
            <span>{pool.shaft?.mine?.name || t('pool_list_no_mine')}</span>
          </div>
          <div className="details-row">
            <span className="details-label">{t('pool_detail_label_shaft')}</span>
            <span>{pool.shaft?.name || t('pool_list_no_shaft')}</span>
          </div>
          <div className="details-row">
            <span className="details-label">{t('pool_detail_label_pump_count')}</span>
            <span>
              {pumps.length} {t('pool_detail_pump_count_unit')}
            </span>
          </div>
        </div>

        <div className="pool-pumps-section">
          <h3 className="section-title">{t('pool_detail_section_pumps')}</h3>
          {pumps.length > 0 ? (
            <div className="pumps-list">
              {pumps.map((pump) => (
                <div
                  key={pump.id}
                  onClick={() => handlePumpClick(pump.id)}
                  style={{ cursor: 'pointer' }}
                  className="pump-item"
                >
                  <div className="pump-card d-flex justify-content-between align-items-center p-3 border-bottom">
                    <div>
                      <div className="pump-name font-weight-bold">{pump.name}</div>
                      <div className="pump-details text-muted small">
                        {t('pool_detail_serial_model', {
                          serial: pump.serial_number,
                          model: pump.model || t('pool_detail_no_model'),
                        })}
                      </div>
                    </div>
                    {canReplacePumpInPool && (
                      <button
                        type="button"
                        className="btn-vaihda"
                        onClick={(e) => handleVaihdaClick(e, pump.id)}
                        title={t('pool_detail_replace_title')}
                      >
                        <i className="fas fa-exchange-alt" aria-hidden />
                        <span>{t('pool_detail_replace_btn')}</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
              {t('pool_detail_no_pumps')}
            </div>
          )}
        </div>
      </div>

      {canEditPoolMetadata && (
        <EditPoolModal
          isOpen={showEditModal}
          poolId={poolId}
          initialData={pool}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updatedPool) => {
            setShowEditModal(false);
            setPool(updatedPool);
          }}
        />
      )}
    </div>
  );
}
